class ImageSliderComponent extends HTMLElement {
  constructor() {
    super();

    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.initSlider();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);

    if (this.splide) {
      this.splide.destroy(true);
      this.splide = null;
    }
  }

  getInteger(name, fallback) {
    const value = Number.parseInt(this.dataset[name], 10);
    return Number.isNaN(value) ? fallback : value;
  }

  getNumber(name, fallback) {
    const value = Number.parseFloat(this.dataset[name]);
    return Number.isNaN(value) ? fallback : value;
  }

  initSlider() {
    this.sliderContainer = this.querySelector('.splide');

    if (!this.sliderContainer || typeof Splide === 'undefined') return;

    if (this.splide) {
      this.splide.destroy(true);
    }

    const type = this.dataset.type || 'slide';
    const typeMobile = this.dataset.typeMobile || type;
    const isLoop = type === 'loop' || typeMobile === 'loop';
    const slidesDesktop = this.getInteger('slidesDesktop', 3);
    const slidesMobile = this.getInteger('slidesMobile', 1);
    const perMoveDesktop = this.getInteger('perMoveDesktop', 1);
    const perMoveMobile = this.getInteger('perMoveMobile', 1);
    const originalSlideCount = this.sliderContainer.querySelectorAll(
      '.splide__list > .splide__slide:not(.splide__slide--clone)'
    ).length;

    // Explicit clones prevent a visible rewind when desktop/mobile column and
    // per-move values require more loop buffer than Splide calculates itself.
    const loopClones = isLoop && originalSlideCount > 1
      ? Math.max(
          originalSlideCount,
          slidesDesktop,
          slidesMobile,
          perMoveDesktop,
          perMoveMobile
        )
      : undefined;

    const focus = this.dataset.focus || 0;
    const arrowsColor = this.dataset.arrowsColor
      ? ` color-${this.dataset.arrowsColor}`
      : '';
    const dotsColor = this.dataset.dotsColor
      ? ` color-${this.dataset.dotsColor} dots-custom-color`
      : '';
    const paddingCalcDesktop = this.dataset.paddingCalcDesktop === 'true';
    const paddingCalcMobile = this.dataset.paddingCalcMobile === 'true';
    const rightPaddingDesktop = this.getInteger('sidePaddingDesktop', 0);
    const rightPaddingMobile = this.getInteger('sidePaddingMobile', 0);

    const config = {
      type,
      direction: typeof defaultDirection === 'undefined' ? 'ltr' : defaultDirection,
      rewind: type === 'fade',
      clones: loopClones,
      autoplay: this.dataset.autoplay === 'true',
      interval: this.getNumber('autoplaySpeed', 5) * 1000,
      drag:
        this.dataset.drag === 'false'
          ? false
          : this.dataset.drag === 'free'
            ? 'free'
            : true,
      focus,
      trimSpace: this.dataset.trimSpace !== 'false',
      arrows: this.dataset.arrows !== 'false',
      pagination: this.dataset.pagination !== 'false',
      omitEnd: this.dataset.omitEnd !== 'false',
      perPage: slidesDesktop,
      perMove: perMoveDesktop,
      autoWidth: this.dataset.autoWidth === 'true',
      gap: this.getInteger('gapDesktop', 30),
      paddingCalc: paddingCalcDesktop,
      padding: {
        left: paddingCalcDesktop ? 0 : rightPaddingDesktop,
        right: rightPaddingDesktop,
      },
      destroy: this.dataset.destroyDesktop === 'true',
      classes: {
        arrow: `splide__arrow${arrowsColor}`,
        page: `splide__pagination__page${dotsColor}`,
      },
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      breakpoints: {
        749: {
          type: typeMobile,
          rewind: typeMobile === 'fade',
          clones: loopClones,
          omitEnd: this.dataset.omitEnd !== 'false',
          perPage: slidesMobile,
          perMove: perMoveMobile,
          gap: this.getInteger('gapMobile', 15),
          paddingCalc: paddingCalcMobile,
          padding: {
            left: paddingCalcMobile ? 0 : rightPaddingMobile,
            right: rightPaddingMobile,
          },
          destroy: this.dataset.destroyMobile === 'true',
        },
      },
    };

    this.splide = new Splide(this.sliderContainer, config);

    this.splide.on('mounted move', () => {
      this.updateActiveSlide();
    });

    if (this.dataset.pauseVideos === 'true') {
      this.splide.on('hidden', (slide) => {
        const video = slide.slide.querySelector('internal-video');
        if (!video) return;

        if (video.dataset.actionOnInactive === 'pause') {
          video.video.pause();
          video.classList.remove('internal-video--playing');
        } else if (video.dataset.actionOnInactive === 'mute') {
          video.video.muted = true;
          video.classList.add('internal-video--muted');
        }
      });
    }

    this.splide.mount();
    window.removeEventListener('resize', this.handleResize);
    window.addEventListener('resize', this.handleResize);
  }

  handleResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => this.updateActiveSlide(), 200);
  }

  updateActiveSlide() {
    if (!this.splide) return;

    const slides = this.splide.Components.Elements.slides;
    const activeSlide = slides[this.splide.index];
    if (!activeSlide) return;

    slides.forEach((slide) => slide.classList.remove('is-instant-active'));
    activeSlide.classList.add('is-instant-active');

    const image = activeSlide.querySelector('img');
    if (image && !image.complete) {
      image.addEventListener('load', () => this.setActiveSlideHeight(activeSlide), {
        once: true,
      });
      return;
    }

    this.setActiveSlideHeight(activeSlide);
  }

  setActiveSlideHeight(slide) {
    if (slide) {
      this.style.setProperty('--active-slide-height', `${slide.offsetHeight}px`);
    }
  }
}

if (!customElements.get('image-slider-component')) {
  customElements.define('image-slider-component', ImageSliderComponent);
}
